/**
 * Currencies.
 *
 * ILS is the base. Everything in the books is ILS agorot. Foreign cash is
 * converted at the rate the owner set for that day, and EVERY sale stores the
 * exact rate it used, so a receipt can always be re-checked years later.
 *
 * A rate is stored as "micros": ILS per 1 foreign unit x 1,000,000.
 * 3.725 ILS per USD  ->  3_725_000. Integers again, so no drift.
 */
import { type Minor, assertMinor, roundHalfUp } from './money.js';

export interface Currency {
  code: string;
  symbol: string;
  /** Minor units per major unit: 2 for ILS/USD/EUR, 3 for JOD. */
  decimals: number;
  /** ILS per 1 unit of this currency, x 1,000,000. ILS itself is 1_000_000. */
  rateMicros: number;
  active: boolean;
  /** Cash in this currency rounds to this many minor units (ILS: 10 = 0.10). */
  cashStep: number;
}

export const MICROS = 1_000_000;

export const BASE_CURRENCY = 'ILS';

export const DEFAULT_CURRENCIES: Currency[] = [
  { code: 'ILS', symbol: '₪', decimals: 2, rateMicros: 1_000_000, active: true, cashStep: 10 },
  { code: 'USD', symbol: '$', decimals: 2, rateMicros: 3_700_000, active: true, cashStep: 1 },
  { code: 'JOD', symbol: 'JD', decimals: 3, rateMicros: 5_220_000, active: true, cashStep: 10 },
  { code: 'EUR', symbol: '€', decimals: 2, rateMicros: 4_000_000, active: true, cashStep: 1 },
];

export function rateToMicros(rate: number): number {
  if (!Number.isFinite(rate) || rate <= 0) throw new Error('an exchange rate must be greater than zero');
  return Math.round(rate * MICROS);
}

export function microsToRate(micros: number): number {
  return micros / MICROS;
}

/**
 * Foreign minor units -> ILS agorot.
 * 20.00 USD (2000 cents) at 3.70 -> 7400 agorot.
 */
export function toIls(amountMinor: Minor, cur: Pick<Currency, 'decimals' | 'rateMicros'>): Minor {
  assertMinor(amountMinor);
  // amountMinor / 10^dec  * (rateMicros / 1e6)  * 100 agorot
  const divisor = 10 ** cur.decimals * MICROS;
  return roundHalfUp((amountMinor * cur.rateMicros * 100) / divisor);
}

/**
 * ILS agorot -> foreign minor units. Used to show the customer what to hand over.
 * Rounds UP to the currency cash step, because a shop cannot take half a cent.
 */
export function fromIls(
  agorot: Minor,
  cur: Pick<Currency, 'decimals' | 'rateMicros' | 'cashStep'>,
  mode: 'nearest' | 'up' = 'up',
): Minor {
  assertMinor(agorot);
  const exact = (agorot * 10 ** cur.decimals * MICROS) / (cur.rateMicros * 100);
  const step = cur.cashStep || 1;
  return mode === 'up' ? Math.ceil(exact / step) * step : roundHalfUp(exact / step) * step;
}

export function formatCurrency(
  amountMinor: Minor,
  cur: Pick<Currency, 'symbol' | 'decimals' | 'code'>,
  opts: { arabicDigits?: boolean } = {},
): string {
  const neg = amountMinor < 0;
  const abs = Math.abs(amountMinor);
  const d = 10 ** cur.decimals;
  const whole = Math.floor(abs / d);
  const frac = abs % d;
  let body =
    cur.decimals > 0
      ? `${groupThousands(whole)}.${String(frac).padStart(cur.decimals, '0')}`
      : groupThousands(whole);
  if (opts.arabicDigits) body = toArabicDigits(body);
  return `${neg ? '-' : ''}${cur.symbol}${body}`;
}

function groupThousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

export function toArabicDigits(s: string): string {
  return s.replace(/\d/g, (d) => AR_DIGITS[Number(d)]);
}
